import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { fetchAgents } from '@/entities/agent';
import { ApiError } from '@/shared/api';
import { uiCopy } from '@/shared/copy';
import { Alert, AlertDescription, AlertTitle, Button, Skeleton } from '@/shared/ui';
const FIELD_CLASS = 'min-h-[var(--unity-hit-target)] w-full rounded-[var(--unity-radius)] border border-border bg-[var(--unity-color-elevated)] px-3 text-sm text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50';
export function FreeAgentPicker({ busy = false, excludeAgentIds, onPick, }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [query, setQuery] = useState('');
    const excludedKey = useMemo(() => {
        if (!excludeAgentIds)
            return '';
        const ids = excludeAgentIds instanceof Set
            ? [...excludeAgentIds]
            : [...excludeAgentIds];
        return ids.slice().sort().join(',');
    }, [excludeAgentIds]);
    useEffect(() => {
        let cancelled = false;
        void (async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await fetchAgents('all');
                if (!cancelled) {
                    const excluded = new Set(excludedKey.length > 0 ? excludedKey.split(',') : []);
                    setItems(data.items.filter((item) => !excluded.has(item.agentId)));
                }
            }
            catch (err) {
                if (!cancelled) {
                    setError(err instanceof ApiError ? err.message : uiCopy.loadFailed);
                }
            }
            finally {
                if (!cancelled)
                    setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [excludedKey]);
    const filtered = useMemo(() => {
        const needle = query.trim().toLowerCase();
        if (!needle)
            return items;
        return items.filter((agent) => {
            const haystack = `${agent.displayName} ${agent.phone ?? ''} ${agent.agencyName ?? ''} ${agent.assignedManagerName ?? ''}`;
            return haystack.toLowerCase().includes(needle);
        });
    }, [items, query]);
    if (loading)
        return _jsx(Skeleton, { className: "h-40 w-full" });
    if (error) {
        return (_jsxs(Alert, { variant: "destructive", children: [_jsx(AlertTitle, { children: uiCopy.errorTitle }), _jsx(AlertDescription, { children: error })] }));
    }
    if (items.length === 0) {
        return (_jsxs(Alert, { children: [_jsx(AlertTitle, { children: "\u0410\u0433\u0435\u043D\u0442\u043E\u0432 \u0434\u043B\u044F \u0437\u0430\u043A\u0440\u0435\u043F\u043B\u0435\u043D\u0438\u044F \u043D\u0435\u0442" }), _jsx(AlertDescription, { children: "\u0412\u0441\u0435 \u0430\u0433\u0435\u043D\u0442\u044B \u0441\u043F\u0440\u0430\u0432\u043E\u0447\u043D\u0438\u043A\u0430 \u0443\u0436\u0435 \u0443 \u044D\u0442\u043E\u0433\u043E \u0431\u0440\u0435\u043D\u0434-\u043C\u0435\u043D\u0435\u0434\u0436\u0435\u0440\u0430, \u043B\u0438\u0431\u043E \u0441\u043F\u0440\u0430\u0432\u043E\u0447\u043D\u0438\u043A \u043F\u0443\u0441\u0442." })] }));
    }
    return (_jsxs("div", { className: "space-y-3", children: [_jsx("p", { className: "text-sm font-medium", children: uiCopy.pickAgent }), _jsx("input", { type: "search", value: query, onChange: (event) => setQuery(event.target.value), placeholder: "\u041D\u0430\u0439\u0442\u0438 \u0430\u0433\u0435\u043D\u0442\u0430", className: FIELD_CLASS, "aria-label": "\u041D\u0430\u0439\u0442\u0438 \u0430\u0433\u0435\u043D\u0442\u0430" }), filtered.length === 0 ? (_jsx("p", { className: "text-sm text-muted-foreground", children: "\u041D\u0438\u043A\u043E\u0433\u043E \u043D\u0435 \u043D\u0430\u0448\u043B\u0438 \u043F\u043E \u044D\u0442\u043E\u043C\u0443 \u0437\u0430\u043F\u0440\u043E\u0441\u0443." })) : (_jsx("ul", { className: "divide-y divide-border rounded-md border border-border", children: filtered.map((agent) => (_jsx("li", { children: _jsx(Button, { type: "button", variant: "ghost", disabled: busy, className: "h-auto w-full justify-between rounded-none px-3 py-3 text-left", onClick: () => onPick(agent), children: _jsxs("span", { className: "min-w-0", children: [_jsx("span", { className: "block truncate font-medium", children: agent.displayName }), agent.assignedManagerName ? (_jsx("span", { className: "block text-xs font-normal text-muted-foreground", children: agent.assignedManagerName })) : (_jsx("span", { className: "block text-xs font-normal text-muted-foreground", children: "\u0411\u0435\u0437 \u0437\u0430\u043A\u0440\u0435\u043F\u043B\u0435\u043D\u0438\u044F" }))] }) }) }, agent.agentId))) }))] }));
}
